import {
  PageRequest,
  PerformanceAnalysisboardPgPage,
  PerformanceAnalysisboardReq,
  PerformanceAnalysisboardReqParam,
} from '@models/PerformanceAnalysisboard';
import axios from 'axios';
import { environment } from '@environments/environment';
import { getPeriodMetaFactory } from '@utils/PeriodUtils';
import { StackedChartResp } from '@data-visualization/stacked-chart/Types';
import { FilterCriteriaParam } from '@models/CommonParam';
import { applyParamsBasedOnFilterPOST } from './utils/OverviewServiceUtils';
import { PerformanceAnalysisboardParam } from '@data-visualization/performance-analysis-line-chart/Types';
import { sortLegendDataAscending } from '@data-visualization/utilities/ChartUtils';
import { ProductDataType } from '@pages/product-analyzer/Product';
import { getCollectionsLiveDataParams } from '@utils/collectionUtils/CollectionUtils';
import { getCountryCodes } from '@utils/CountryUtils';
import { throwApiError } from '@services/utils/ErrorHandlers';

export const getPerformanceAnalysisBoard = async (
  param: PerformanceAnalysisboardParam,
  pageRequest: PageRequest,
): Promise<PerformanceAnalysisboardPgPage> => {
  let queryParams: any;

  try {
    const sortKey = param.sortOption.label?.toUpperCase();
    const {
      clientId,
      productType,
      countryIds,
      productGroup,
      period,
      comparisonPeriod,
      periodType,
      shareType,
      sortOption,
      growthType,
      appliedFilters,
      segment,
      dataType,
    } = param;
    const performanceReqParam: PerformanceAnalysisboardReqParam = {
      clientId,
      productType,
      countryIds,
      productGroupForPA: productGroup,
      period,
      comparisonPeriod,
      periodTypeForPA: periodType,
      shareType,
      appliedFilters,
      segment,
      dataType,
    };
    queryParams = constructRequest(performanceReqParam);

    const { data } = await axios.post<PerformanceAnalysisboardPgPage>(
      `${environment.baseUrl}/v2/performance-analysis/analysis-board?page=${pageRequest.page}&size=${
        pageRequest.size
      }&sortBy=${sortOption.headerKey}&sortDirection=${
        sortOption.sortDir
      }&sortKey=${sortKey}&growthType=${growthType.toUpperCase()}&clientId=${clientId}&country=${countryIds}&periodType=${periodType.toUpperCase()}`,
      queryParams,
    );

    return data;
  } catch (err: any) {
    // Delegated error handling; helper always throws. Added explicit throw for Sonar compliance.
    throwApiError(err, { excludedBrand: queryParams?.excludedBrand }); // NOSONAR
  }
};

/**
 * Returns array of elements if found, else empty array.
 */
export const getStackedChart = async (param: FilterCriteriaParam): Promise<StackedChartResp[]> => {
  try {
    const performanceAnalysisboardReqParam: PerformanceAnalysisboardReqParam = {
      clientId: param.clientId,
      productType: param.productType,
      countryIds: getCountryCodes(param.countries),
      productGroupForPA: param.productGroup,
      period: param.period,
      comparisonPeriod: param.comparisonPeriod,
      periodTypeForPA: param.periodType,
      shareType: param.shareType,
      appliedFilters: param.appliedFilters,
      segment: param.segmentName,
      dataType: param.segmentDataType,
      kpi: param.kpi,
      segmentBy: param.segmentBy,
      segmentByDataType: param.segmentByDataType,
      isUsingCollectionLiveData: param.isUsingCollectionLiveData,
    };
    const { data } = await axios.post<StackedChartResp[]>(
      `${environment.baseUrl}/v2/performance-analysis/stacked-chart`,
      constructRequest(performanceAnalysisboardReqParam),
      {
        params: {},
      },
    );
    return data.length > 0 ? sortLegendDataAscending(data) : [];
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

const constructRequest = ({
  clientId,
  productType,
  countryIds,
  productGroupForPA,
  period,
  comparisonPeriod,
  periodTypeForPA,
  shareType,
  appliedFilters,
  segment,
  dataType,
  kpi,
  segmentBy,
  segmentByDataType,
  isUsingCollectionLiveData = false,
}: PerformanceAnalysisboardReqParam): PerformanceAnalysisboardReq => {
  const req = {} as PerformanceAnalysisboardReq;
  const periodMetaForPA = getPeriodMetaFactory(period, comparisonPeriod, periodTypeForPA);

  req.clientId = clientId;
  req.productType = productType;
  req.productDataType = ProductDataType.PerformanceAnalyzer;
  req.country = countryIds;

  if (productGroupForPA && productGroupForPA.length !== 0) {
    req.productGroup = productGroupForPA.map((option) => {
      return option.value;
    });
  }

  req.periodType = periodTypeForPA ? periodTypeForPA.replace(/ /g, '').toUpperCase() : '';
  req.period = periodMetaForPA ? periodMetaForPA.currentPeriod.toUpperCase() : '';
  req.comparisonPeriod = periodMetaForPA ? periodMetaForPA.comparisonPeriod.toUpperCase() : '';
  req.shareType = shareType;
  //Temporary
  req.showPerformance = segment;
  req.dataType = dataType;
  req.kpi = kpi;
  req.segmentBy = segmentBy;
  req.segmentByDataType = segmentByDataType;
  applyParamsBasedOnFilterPOST(req, appliedFilters);
  const collectionsParam = getCollectionsLiveDataParams(isUsingCollectionLiveData, periodTypeForPA, comparisonPeriod);
  return {
    ...req,
    ...collectionsParam,
  };
};
